import {Locale} from '@/utils/constants'
import {Region} from '@/utils/countries'
import {Jurisdiction} from '@/utils/countries/constants'
import {jurisdictionsData} from '@/utils/jurisdictions/constants'
import {DEFAULT_HOST, routeTo} from '@/utils/router'
import {Service} from '@/utils/service-request/types'
import {tmRenewalJurisdictionToHeroImage} from '@/utils/trademark-renewal/constants'

import {getImageLocale} from '../utils/helpers'

export const getTrademarkRenewalJurisdictionContent = (
  jurisdictionCode: Jurisdiction,
  locale: Locale,
) => {
  const jurisdiction = jurisdictionCode.toLowerCase()
  const ogImageLocale = getImageLocale(locale)

  const normalizedRegion =
    jurisdictionCode === Jurisdiction.UK ? Region.GB : (jurisdictionCode as unknown as Region)

  const defaultRegion = Object.values(Region).includes(normalizedRegion)
    ? normalizedRegion
    : Region.EU

  const {
    registrationTime,
    renewal: {earlyFiling, gracePeriod},
  } = jurisdictionsData()[defaultRegion]

  return {
    metaTitle: `tm-renewal.meta-title-${jurisdiction}`,
    metaDescription: `tm-renewal.meta-desc-${jurisdiction}`,
    ogImage: {
      url: `${DEFAULT_HOST}/images/og-images/trademark-renewal-${ogImageLocale}.png`,
      width: 1200,
      height: 627,
      alt: 'Trademark renewal - OG image',
    },
    hero: {
      title: 'tm-renewal.jurisdiction-hero-title',
      imageSrc: tmRenewalJurisdictionToHeroImage[jurisdictionCode]
        ? `/images/hero/trademark-renewal/${tmRenewalJurisdictionToHeroImage[jurisdictionCode]}`
        : '/images/hero/trademark-renewal/general.svg',
      imageAlt: `Trademark renewal in ${jurisdiction}`,
      primaryButtonLink: routeTo.trademarkRenewal.form({jurisdictions: jurisdiction}),
    },
    cards: {
      title: 'brand-protection-cards-title',
      services: {
        [Service.tmRenewal]: {
          serviceTitle: 'Trademark renewal',
          title: 'legal-services-tm-renewal-title',
          imageSrc: '/images/icons/arrows-in-circle.svg',
          imageAlt: 'Two arrows in a circle',
          badgeText: 'tm-renewal.simple-online-process-title',
          bulletPoints: [
            ...(earlyFiling ? [{text: 'tm-renewal.early-filing-desc', number: earlyFiling}] : []),
            ...(gracePeriod ? [{text: 'tm-renewal.grace-period-desc', number: gracePeriod}] : []),
          ],
          buttonText: 'button-start-with-tm-renewal',
          buttonLinkHref: routeTo.trademarkRenewal.form({jurisdictions: jurisdiction}),
        },
        [Service.tmRegistration]: {
          serviceTitle: 'Trademark registration',
          title: 'legal-services-trademark-registration-title',
          imageSrc: '/images/icons/r-in-circle.svg',
          imageAlt: 'R in a circle',
          badgeText: 'free-lawyers-check-title',
          bulletPoints: [
            ...(registrationTime
              ? [{text: 'tm-renewal.trademark-registration-list-item-0', number: registrationTime}]
              : []),
            {text: 'tm-renewal.trademark-registration-list-item-1'},
          ],
          buttonText: 'tm-renewal.start-trademark-registration-title',
          buttonLinkHref: routeTo.verification.form({country: jurisdiction}),
        },
        [Service.tmAssignmentAgreement]: {
          serviceTitle: 'Ownership transfer',
          title: 'legal-services-ownership-transfer-title',
          imageSrc: '/images/icons/star-in-circle.svg',
          imageAlt: 'Star in a circle',
          badgeText: 'tm-renewal.contract-included-title',
          bulletPoints: [
            {text: 'tm-renewal.change-of-ownership-transfer-list-item-0'},
            {text: 'tm-renewal.change-of-ownership-transfer-list-item-1'},
          ],
          buttonText: 'start-ownership-transfer-title',
          buttonLinkHref: routeTo.trademarkOwnershipTransfer.form({jurisdictions: jurisdiction}),
        },
      },
      footer: {
        buttonText: 'tm-renewal.e2e-protection-cta',
        buttonLinkHref: routeTo.legalServices(),
      },
    },
  }
}

export const getTrademarkRenewalContent = (locale: Locale) => {
  const ogImageLocale = getImageLocale(locale)

  return {
    metaTitle: 'tm-renewal.meta-title',
    metaDescription: 'tm-renewal.meta-desc',
    ogImage: {
      url: `${DEFAULT_HOST}/images/og-images/trademark-renewal-${ogImageLocale}.png`,
      width: 1200,
      height: 627,
      alt: 'Trademark renewal - OG image',
    },
    header: {
      buttonLinkText: 'tm-renewal.header-main-cta',
      buttonLinkHref: routeTo.trademarkRenewal.form(),
    },
    hero: {
      title: 'tm-renewal.hero-title',
      subtitle: 'tm-renewal.hero-subtitle',
      imageSrc: '/images/hero/trademark-renewal/general.svg',
      imageAlt: 'Trademark-renewal',
      primaryButtonText: 'button-start-with-tm-renewal',
      primaryButtonLink: routeTo.trademarkRenewal.form(),
      list: [
        'tm-renewal.hero-list-item-0',
        'tm-renewal.hero-list-item-1',
        'tm-renewal.hero-list-item-2',
      ],
    },
    cards: {
      title: 'tm-renewal.countries-title',
      description: 'tm-renewal.countries-desc',
      badgeText: 'tm-renewal.no-hidden-costs-title',
      buttonText: 'button-start-with-tm-renewal',
      footer: {
        title: 'tm-renewal.all-countries-title',
        description: 'tm-renewal.all-countries-desc',
        buttonText: 'show-all-countries-text',
        buttonLinkHref: routeTo.trademarkRenewal.form(),
      },
    },
    howItWorks: {
      title: 'how-it-works-title',
      steps: [
        {
          title: 'tm-renewal.how-it-works-step-0-title',
          description: 'tm-renewal.how-it-works-step-0-desc',
          imageSrc: '/images/icons/response.svg',
          imageAlt: 'Trademark renewal request',
        },
        {
          title: 'tm-renewal.how-it-works-step-1-title',
          description: 'tm-renewal.how-it-works-step-1-desc',
          imageSrc: '/images/icons/assessment.svg',
          imageAlt: 'Power of Attorney',
        },
        {
          title: 'tm-renewal.how-it-works-step-2-title',
          description: 'tm-renewal.how-it-works-step-2-desc',
          imageSrc: '/images/icons/registration-support.svg',
          imageAlt: 'Submission',
        },
        {
          title: 'tm-renewal.how-it-works-step-3-title',
          description: 'tm-renewal.how-it-works-step-3-desc',
          imageSrc: '/images/icons/documents-with-check-icon.svg',
          imageAlt: 'Renewed trademark',
        },
      ],
    },
    portalOverview: {
      title: 'tm-renewal.portal-overview-title',
      description: 'tm-renewal.portal-overview-desc',
      list: [
        'tm-renewal.portal-overview-list-item-0',
        'tm-renewal.portal-overview-list-item-1',
        'tm-renewal.portal-overview-list-item-2',
      ],
      buttonLinkHref: routeTo.portal.home(),
      buttonLinkText: 'tm-renewal.portal-overview-cta',
      imageSrc: '/images/portal-overview.png',
      imageAlt: 'Customer portal dashboard',
    },
    getStartedBanner: {
      title: 'get-started-banner-title',
      imageSrc: '/images/ice-cream-melt.svg',
      imageAlt: 'Ice cream melt',
      primaryButtonText: 'button-start-with-tm-renewal',
    },
    success: {
      metaTitle: 'tm-renewal.success-meta-title',
      metaDescription: 'tm-renewal.success-meta-desc',
      message: {
        title: 'tm-renewal.success-title',
        descripton: 'tm-renewal.success-desc',
      },
    },
  }
}
